import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Employee } from '@/api/models';
import type { EmployeeWriteRequest } from '@/api/handlers/resources/salaries/requests';
import {
    useCreateEmployeeMutation,
    useDeleteEmployeeMutation,
    useEmployeesQuery,
    useUpdateEmployeeMutation,
} from '@/api/queries';
import { parseGelToTetri } from '@/pages/accounting/books/chartOfAccounts/utils';
import { newEntryId } from '@/pages/accounting/journalEntry/utils';
import {
    isPersonalIdTaken,
    isValidPersonalId,
    salaryErrorMessage,
} from '../../salariesMeta';

/**
 * "A write landed", for a host that wants to confirm it.
 *
 * `meta.alreadyExisted` comes from the create response: the id is generated
 * client-side and the endpoint is idempotent on it, so a retried submission
 * returns the original row and hires nobody. Reporting that as a fresh addition
 * would tell someone they had taken on two people when they had taken on one —
 * the same distinction the Record tab draws between *posted* and *replayed*.
 */
export type EmployeeSaved = (
    employee: Employee,
    meta: { isEdit: boolean; alreadyExisted: boolean },
) => void;

type UseEmployeeFormParams = {
    /** The employee being edited; `null` = create. */
    employee: Employee | null;
    /** Close the host drawer. */
    onClose: () => void;
    /**
     * A create or an update succeeded. Deliberately separate from `onClose`,
     * which also fires on Cancel, on the backdrop, and after a delete — a host
     * that treated closing as success would confirm an abandoned form.
     */
    onSaved?: EmployeeSaved;
};

export type EmployeeFormModel = {
    isEdit: boolean;
    nameKa: string;
    nameEn: string;
    personalId: string;
    positionKa: string;
    positionEn: string;
    hireDate: string;
    terminationDate: string;
    grossSalary: string;
    pensionParticipant: boolean;
    pensionNote: string;
    onNameKaChange: (value: string) => void;
    onNameEnChange: (value: string) => void;
    onPersonalIdChange: (value: string) => void;
    onPositionKaChange: (value: string) => void;
    onPositionEnChange: (value: string) => void;
    onHireDateChange: (value: string) => void;
    onTerminationDateChange: (value: string) => void;
    onGrossSalaryChange: (value: string) => void;
    onPensionParticipantChange: (value: boolean) => void;
    onPensionNoteChange: (value: string) => void;
    /** True once a termination date is set — drives the confirmation copy. */
    willTerminate: boolean;
    submitted: boolean;
    /** i18n key per field, or null when the field is fine. */
    errors: Record<string, string | null>;
    isValid: boolean;
    isSaving: boolean;
    saveError: string | null;
    onSubmit: () => Promise<void>;
    /**
     * Delete is armed by a first press and only runs on the second.
     *
     * Not a dialog: the action already lives inside a drawer, and stacking an
     * overlay on an overlay to ask one question is more ceremony than the
     * decision needs. But it is not a bare button either — unlike archiving an
     * account, this cannot be undone from the UI.
     */
    isDeleteArmed: boolean;
    onDeleteArm: () => void;
    onDeleteCancel: () => void;
    isDeleting: boolean;
    deleteError: string | null;
    onDelete: () => Promise<void>;
};

/**
 * The add / edit employee form.
 *
 * Creating or editing an employee produces **no journal entry** — hiring someone
 * is not a transaction, and neither is correcting their job title. The only thing
 * that posts on this screen is a payroll run.
 *
 * Terminating is handled here rather than by a separate action: it is setting a
 * termination date, and `status` follows from it. The employee is never deleted
 * and keeps every figure they earned.
 *
 * The personal ID is checked twice on purpose: for **format** (11 digits) and for
 * **uniqueness**, so the user is told while typing. The server is the final
 * authority and its field errors are mapped back onto the same field, mirroring
 * the account form's code-collision handling.
 *
 * The uniqueness check runs against its **own unfiltered fetch** of the register,
 * not the rows the page happens to be showing. The page list is narrowed by the
 * status filter and the search box, so checking against it would miss a collision
 * with a terminated employee (or anyone the current search excludes) and let the
 * form call itself valid until the server refused it. The query is keyed
 * `ALL`/`''`, so it is shared and cached like any other.
 */
export const useEmployeeForm = ({
    employee,
    onClose,
    onSaved,
}: UseEmployeeFormParams): EmployeeFormModel => {
    const { t } = useTranslation('pages');
    const isEdit = employee !== null;

    const createMutation = useCreateEmployeeMutation();
    const updateMutation = useUpdateEmployeeMutation();
    const deleteMutation = useDeleteEmployeeMutation();

    const registerQuery = useEmployeesQuery({ status: 'ALL', search: '' });
    // Memoised because it feeds a `useMemo` below: a bare `?? []` mints a
    // fresh array on every render, so every dependent recomputes forever.
    const employees: Employee[] = React.useMemo(
        () =>
            registerQuery.data?.success
                ? registerQuery.data.response.items
                : [],
        [registerQuery.data],
    );

    const [nameKa, setNameKa] = React.useState(employee?.name.ka ?? '');
    const [nameEn, setNameEn] = React.useState(employee?.name.en ?? '');
    const [personalId, setPersonalId] = React.useState(
        employee?.personalId ?? '',
    );
    const [positionKa, setPositionKa] = React.useState(
        employee?.position.ka ?? '',
    );
    const [positionEn, setPositionEn] = React.useState(
        employee?.position.en ?? '',
    );
    const [hireDate, setHireDate] = React.useState(employee?.hireDate ?? '');
    const [terminationDate, setTerminationDate] = React.useState(
        employee?.terminationDate ?? '',
    );
    const [grossSalary, setGrossSalary] = React.useState(
        employee?.grossSalary ?? '',
    );
    const [pensionParticipant, setPensionParticipant] = React.useState(
        employee?.pensionParticipant ?? true,
    );
    const [pensionNote, setPensionNote] = React.useState(
        employee?.pensionNote ?? '',
    );
    const [submitted, setSubmitted] = React.useState(false);
    const [saveError, setSaveError] = React.useState<string | null>(null);
    const [isDeleteArmed, setDeleteArmed] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    /**
     * The i18n key for a server-side rejection of the personal ID, cleared on the
     * next keystroke.
     *
     * A key rather than a boolean because the server rejects the field for two
     * different reasons — `DUPLICATE` and `INVALID` (not 11 digits) — and telling
     * someone their ID is "already taken" when the server actually called it
     * malformed sends them off changing a number that was never the problem.
     */
    const [serverIdErrorKey, setServerIdErrorKey] = React.useState<
        string | null
    >(null);

    const onPersonalIdChange = React.useCallback((value: string) => {
        setServerIdErrorKey(null);
        setSaveError(null);
        setPersonalId(value);
    }, []);

    // ── validation ──────────────────────────────────────────────────────────
    const grossParse = parseGelToTetri(grossSalary);

    const errors = React.useMemo<Record<string, string | null>>(() => {
        const grossError = ((): string | null => {
            if (grossParse.ok) {
                return grossParse.tetri === 0
                    ? 'salaries.form.errors.grossRequired'
                    : null;
            }
            switch (grossParse.reason) {
                case 'empty':
                    return 'salaries.form.errors.grossRequired';
                case 'sub-tetri':
                    return 'salaries.form.errors.subTetri';
                case 'negative':
                    return 'salaries.form.errors.negative';
                default:
                    return 'salaries.form.errors.invalidAmount';
            }
        })();

        const personalIdError = (() => {
            if (personalId.trim() === '') {
                return 'salaries.form.errors.personalIdRequired';
            }
            if (!isValidPersonalId(personalId)) {
                return 'salaries.form.errors.personalIdFormat';
            }
            // The server's own verdict wins: it may know a rule this client does
            // not, and it sees the whole register rather than one page of it.
            if (serverIdErrorKey) {
                return serverIdErrorKey;
            }
            if (
                isPersonalIdTaken(personalId, employees, employee?.id ?? null)
            ) {
                return 'salaries.form.errors.personalIdTaken';
            }
            return null;
        })();

        return {
            name:
                nameKa.trim() === '' && nameEn.trim() === ''
                    ? 'salaries.form.errors.nameRequired'
                    : null,
            personalId: personalIdError,
            hireDate:
                hireDate.trim() === ''
                    ? 'salaries.form.errors.hireDateRequired'
                    : null,
            // A termination before the hire date is not a correction, it is two
            // facts that cannot both be true.
            terminationDate:
                terminationDate.trim() !== '' &&
                hireDate.trim() !== '' &&
                terminationDate < hireDate
                    ? 'salaries.form.errors.terminationBeforeHire'
                    : null,
            grossSalary: grossError,
        };
    }, [
        nameKa,
        nameEn,
        personalId,
        serverIdErrorKey,
        employees,
        employee,
        hireDate,
        terminationDate,
        grossParse,
    ]);

    const isValid = Object.values(errors).every((error) => error === null);

    const buildRequest = React.useCallback(
        (id: string): EmployeeWriteRequest => ({
            id,
            name: {
                ...(nameKa.trim() ? { ka: nameKa.trim() } : {}),
                ...(nameEn.trim() ? { en: nameEn.trim() } : {}),
            },
            personalId: personalId.trim(),
            position: {
                ...(positionKa.trim() ? { ka: positionKa.trim() } : {}),
                ...(positionEn.trim() ? { en: positionEn.trim() } : {}),
            },
            hireDate,
            terminationDate:
                terminationDate.trim() === '' ? null : terminationDate,
            grossSalary: grossSalary.trim(),
            pensionParticipant,
            pensionNote: pensionNote.trim(),
        }),
        [
            nameKa,
            nameEn,
            personalId,
            positionKa,
            positionEn,
            hireDate,
            terminationDate,
            grossSalary,
            pensionParticipant,
            pensionNote,
        ],
    );

    const onSubmit = React.useCallback(async () => {
        setSubmitted(true);
        setSaveError(null);
        if (!isValid) {
            return;
        }

        const result = isEdit
            ? await updateMutation.mutateAsync({
                  employeeId: employee.id,
                  body: buildRequest(employee.id),
              })
            : // A client-generated UUID, so a retried create is idempotent.
              await createMutation.mutateAsync(buildRequest(newEntryId()));

        if (!result.success) {
            // Surface a server-side personal-ID rejection on the field itself,
            // mirroring how the account form maps its code collision; anything
            // else becomes the form-level, input-preserving message.
            //
            // The two codes get different copy: `DUPLICATE` means the number
            // belongs to someone else, `INVALID` means it is not a well-formed
            // personal number. Collapsing them would send a user hunting for a
            // clash that does not exist.
            const personalIdCode = result.error.errors?.personalId;
            if (personalIdCode) {
                setServerIdErrorKey(
                    personalIdCode === 'INVALID'
                        ? 'salaries.form.errors.personalIdFormat'
                        : 'salaries.form.errors.personalIdTaken',
                );
                return;
            }
            setSaveError(result.error.message ?? t('salaries.form.saveFailed'));
            return;
        }
        // Before the close, and only here: this is the one branch that knows a
        // write landed. Both responses carry the saved row; only a create can
        // have been replayed.
        onSaved?.(result.response.employee, {
            isEdit,
            // A create can be replayed; an update cannot, so the flag is
            // only ever on the create response.
            alreadyExisted:
                'alreadyExisted' in result.response &&
                result.response.alreadyExisted === true,
        });
        onClose();
    }, [
        isValid,
        isEdit,
        employee,
        buildRequest,
        updateMutation,
        createMutation,
        onClose,
        onSaved,
        t,
    ]);

    /**
     * Remove an employee outright.
     *
     * Offered without a pre-check, deliberately. Whether an employee is
     * removable depends on whether any run ever *charged* them, and the client
     * cannot know that without reading every run's breakdown — a second
     * implementation of a rule the server already owns, and one that would go
     * stale the moment a run is committed in another tab. So the button is
     * always live and the server is the authority: `EMPLOYEE_HAS_RUNS` comes
     * back with the periods named, and its copy points at termination, which is
     * what the user almost certainly wants instead.
     */
    const onDelete = React.useCallback(async () => {
        if (!isEdit) {
            return;
        }
        setDeleteError(null);
        const result = await deleteMutation.mutateAsync(employee.id);
        if (!result.success) {
            setDeleteError(salaryErrorMessage(result.error, t));
            setDeleteArmed(false);
            return;
        }
        onClose();
    }, [isEdit, employee, deleteMutation, onClose, t]);

    return {
        isEdit,
        nameKa,
        nameEn,
        personalId,
        positionKa,
        positionEn,
        hireDate,
        terminationDate,
        grossSalary,
        pensionParticipant,
        pensionNote,
        onNameKaChange: setNameKa,
        onNameEnChange: setNameEn,
        onPersonalIdChange,
        onPositionKaChange: setPositionKa,
        onPositionEnChange: setPositionEn,
        onHireDateChange: setHireDate,
        onTerminationDateChange: setTerminationDate,
        onGrossSalaryChange: setGrossSalary,
        onPensionParticipantChange: setPensionParticipant,
        onPensionNoteChange: setPensionNote,
        willTerminate:
            terminationDate.trim() !== '' &&
            employee?.terminationDate !== terminationDate,
        submitted,
        errors,
        isValid,
        isSaving: createMutation.isPending || updateMutation.isPending,
        saveError,
        onSubmit,
        isDeleteArmed,
        onDeleteArm: () => {
            setDeleteError(null);
            setDeleteArmed(true);
        },
        onDeleteCancel: () => setDeleteArmed(false),
        isDeleting: deleteMutation.isPending,
        deleteError,
        onDelete,
    };
};
